import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { Pagina, TipoLineaMaterial } from '@cmms/shared';
import { api, consulta } from '../servicios/api.js';
import { Paginacion } from './piezas.js';

interface ElementoListado {
  id: string;
  nombre: string;
  tipo: TipoLineaMaterial;
  tipoActivoId: string | null;
  tipoActivo: string | null;
  creadoEl: string;
  usosOt: number;
  usosSolicitud: number;
}

const ETIQUETA_TIPO: Record<TipoLineaMaterial, string> = {
  HERRAMIENTA: 'Herramienta',
  MATERIAL: 'Material',
};

const TAMANO = 25;

/**
 * Todo lo creado como material o herramienta desde las solicitudes y las OT, en una
 * tabla con búsqueda, filtro por tipo y paginación. Cada fila lleva a su detalle, donde
 * se ve en qué OT y solicitudes se planeó. Ninguno está ligado a un activo salvo que se indique.
 */
export function MaterialesCreados(): JSX.Element {
  const [buscar, setBuscar] = useState('');
  const [tipo, setTipo] = useState('');
  const [pagina, setPagina] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['materiales-catalogo', 'listado', { buscar, tipo, pagina }],
    queryFn: () =>
      api.get<Pagina<ElementoListado>>(
        `/materiales-catalogo/listado${consulta({ buscar: buscar.trim(), tipo, pagina, tamano: TAMANO })}`,
      ),
    placeholderData: (anterior) => anterior,
  });

  return (
    <section className="panel" style={{ marginBottom: 24 }}>
      <div className="panel-cabecera">
        <h2>Materiales y herramientas creados</h2>
      </div>
      <div className="panel-cuerpo">
        <div className="rejilla-campos" style={{ marginBottom: 12 }}>
          <div className="campo">
            <label htmlFor="materiales-creados-buscar">Buscar por nombre</label>
            <input
              id="materiales-creados-buscar"
              value={buscar}
              onChange={(e) => {
                setBuscar(e.target.value);
                setPagina(1);
              }}
            />
          </div>
          <div className="campo">
            <label htmlFor="materiales-creados-tipo">Tipo</label>
            <select
              id="materiales-creados-tipo"
              value={tipo}
              onChange={(e) => {
                setTipo(e.target.value);
                setPagina(1);
              }}
            >
              <option value="">Todos</option>
              <option value="MATERIAL">Material</option>
              <option value="HERRAMIENTA">Herramienta</option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <p className="campo-nota">Cargando…</p>
        ) : error ? (
          <p className="campo-nota">No se pudo cargar el listado.</p>
        ) : !data || data.total === 0 ? (
          <p className="campo-nota">No hay materiales ni herramientas que coincidan.</p>
        ) : (
          <>
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th>Tipo de activo</th>
                    <th>Creado el</th>
                    <th>Planeado en</th>
                  </tr>
                </thead>
                <tbody>
                  {data.datos.map((e) => (
                    <tr key={e.id}>
                      <td>
                        <Link to={`/listas-materiales/materiales/${e.id}`}>{e.nombre}</Link>
                      </td>
                      <td>{ETIQUETA_TIPO[e.tipo]}</td>
                      <td>{e.tipoActivo ?? 'Todos los activos'}</td>
                      <td>{new Date(e.creadoEl).toLocaleDateString('es-MX')}</td>
                      <td>
                        {e.usosOt + e.usosSolicitud === 0 ? (
                          <span className="campo-nota">Ninguna</span>
                        ) : (
                          <span>
                            {e.usosOt} OT · {e.usosSolicitud} solicitud(es)
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paginacion pagina={data.pagina} tamano={data.tamano} total={data.total} onCambiar={setPagina} />
          </>
        )}
      </div>
    </section>
  );
}
